import React, { useEffect, useState } from 'react';
import {
  getGroupBooking,
  addComment,
  bareVenueRequest,
  populateVenueRequest,
} from '@go-client/lib/api/groupBooking';

import {
  BareGroupBooking,
  VenueRequestComment,
  VenueRequest,
  VenueRequestStatus,
  Resource,
} from '@go-shared/types/groupBooking';
import { BubbleButton, BubbleFormGroup, BubbleOverlay } from './BubbleUI';
import {
  timeToString,
  addMinutesToTime,
  findMinMaxDate,
  calculateTimeOffset,
  dateToIsoUTC,
  getLocaleDateStringUTC,
  DEFAULT_TIMESTR_FORMAT,
} from '@go-shared/lib/time';
import { Property } from '@go-shared/types/property';
import { User } from '@go-shared/types/user';
import GroupRequestFormBody from './GroupRequestFormBody';
import { GroupBookingsGrid } from '.';
import { BareSimpleSailingDayBundle, SimpleSailingDayBundle } from '@go-shared/types/sailing';
import { getSimpleSailingsByProperty } from '@go-client/lib/api/itinerary';
import { getVenueTemplateById, populate } from '@go-client/lib/api/venueTemplate';

import './GroupsGlance.scss';
import { makeTextFromStatus } from '@go-client/pages/GroupsHome';
import { findSailingBundleByDateAndDay, getDayLocation } from '@go-shared/lib/sailing';
import { bulkBookingStatusChange, ResourceDataSchema } from './GroupBookingForm/Workflows';
import { safeIdCompare, makeFullName } from '@go-shared/lib/utils';
import { onlyUnique } from '@go-shared/lib/functional';
import { getManyUsersById } from '@go-client/lib/api/user';
import { DefaultedText } from './DefaultedText';
import { canApproveOrRejectABooking } from '@go-shared/lib/permissions';
import { getHourFormat } from '@go-shared/lib/property';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { getCommonTexts } from '@go-shared/lib/brand';
import { formatOtherResources } from '@go-shared/lib/groupBooking';
import {
  GroupResourceType,
  GroupResourceTypeList,
  PhotographyGroupResourceTypeList as PhotographyGroupResource,
} from '@go-shared/types/zod/groupResource';
import { safeFetch } from '@go-client/lib/api';
import { groupResourceClient } from '@go-client/lib/api/v2/groupResource';

interface GroupsGlanceProps {
  user: User;
  property: Property;
  groupId: string;
  bookingId: string;
  hideComments?: boolean;
  hideVenueDayView?: boolean;
  hideStatusChange?: boolean;
  data?: GroupsGlanceData;
  onFetchData?: () => void;
}

interface GroupsGlanceData {
  group: BareGroupBooking;
  booking: VenueRequest;
  sailingBundle: SimpleSailingDayBundle | BareSimpleSailingDayBundle | undefined;
}

const excludedTypes = [...GroupResourceTypeList, ...PhotographyGroupResource];

export default function GroupsGlance(props: GroupsGlanceProps) {
  const hourFormat = getHourFormat(props.property);

  const [glanceData, setGlanceData] = useState<GroupsGlanceData | undefined>(undefined);
  const [showContext, setShowContext] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [showMoreGroupDetails, setShowMoreGroupDetails] = useState(false);
  const [showMoreBookingDetails, setShowMoreBookingDetails] = useState(false);
  const [showMoreSubgroupDetails, setShowMoreSubgroupDetails] = useState(false);
  const { languages, defaultLanguage, brand } = useServerInfo();

  useEffect(() => {
    initialize().catch(console.error);
  }, [props.data]);

  async function initialize() {
    setShowContext(false);
    const resolvedData = props.data || (await fetchDataBundle());
    setGlanceData(resolvedData);
  }

  async function fetchDataBundle() {
    const groupBooking = await getGroupBooking(props.groupId);

    const bareRequest = groupBooking.requests.find(r => safeIdCompare(r._id, props.bookingId));
    if (!bareRequest) throw new Error('Could not find request of given venue request id!');

    const [since, until] = findMinMaxDate(bareRequest.date);
    const simpleSailings = await getSimpleSailingsByProperty(
      props.property._id,
      since ? dateToIsoUTC(since) : undefined,
      until ? dateToIsoUTC(until) : undefined
    );

    const bareVenueTemplate = await getVenueTemplateById(props.property._id, bareRequest.venue);
    const venueTemplate = populate(bareVenueTemplate, props.property);

    const requestUsers = await getManyUsersById(
      [groupBooking.createdBy, bareRequest.createdBy].filter(onlyUnique)
    );

    const { resources: groupResources } = await safeFetch(
      groupResourceClient.getResourcesAndTypes({
        params: { brandId: props.property.brand._id },
      }),
      ResourceDataSchema
    );

    const request = populateVenueRequest(
      bareRequest,
      requestUsers,
      [venueTemplate],
      groupBooking,
      groupResources
    );

    return {
      group: groupBooking,
      booking: request,
      sailingBundle: findSailingBundleByDateAndDay(request.date, request.dayIndex, simpleSailings),
    };
  }

  async function handleRequestComment(message: string) {
    if (!glanceData) return;

    const comment: VenueRequestComment = {
      message,
      date: new Date(),
      user: {
        _id: props.user._id,
        firstName: props.user.firstName,
        lastName: props.user.lastName,
        email: props.user.email,
      },
    };

    await addComment(glanceData.group, bareVenueRequest(glanceData.booking), comment);

    if (props.onFetchData) props.onFetchData();

    // reinitialize if parent is not providing info
    if (!props.data) await initialize();
  }

  async function handleRequestStatusChange(status: VenueRequestStatus) {
    if (!glanceData) return;

    setIsBusy(true);

    try {
      await bulkBookingStatusChange(
        [{ group: glanceData.group, booking: bareVenueRequest(glanceData.booking) }],
        props.user,
        status
      );

      if (props.onFetchData) props.onFetchData();

      // reinitialize if parent is not providing info
      if (!props.data) await initialize();
    } finally {
      setIsBusy(false);
    }
  }

  // this allows the component to render immediately in cases where the data is
  // provided by the parent
  const data = glanceData || props.data;

  const canApprove =
    !props.hideStatusChange && data ? canApproveOrRejectABooking(props.user, data.booking) : false;
  const cols = props.hideComments ? 2 : 3;

  const {
    guest_experience,
    tour_leader,
    tour_leader_cabin,
    hospitality_desk,
    embark_date,
    debark_date,
    guests,
    agency_contact_name,
    sales_dept_email,
    sales_dept_phone,
    party_name,
    group_id,
    group_type,
    cabin_drops,
    dining_preference,
    prepaid_packages,
    sales_office,
    group_notes,
    group_files,
    group_contact_name,
  } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  function otherGroupResourceSection(resources: Resource[]) {
    const otherResources = resources.filter(r => !excludedTypes.includes(r.resourceType));
    if (otherResources.length === 0)
      return (
        <div className="mr-3">
          <span className="text-smoke italic">None</span>
        </div>
      );
    return formatOtherResources(otherResources).flatMap(({ type, items }, groupIndex) => [
      <DefaultedText key={type} val={`${type}: `} />,
      ...items.map((r, index) => {
        const text = `${r.name} ($${r.price}) (amt. ${r.amount})`;
        return (
          <DefaultedText
            key={`${type}-${index}`}
            val={index < items.length - 1 ? `${text}, ` : text}
          />
        );
      }),
      <br key={`br-${groupIndex}`} />,
    ]);
  }

  function groupBookingCreatedAtSection(data: VenueRequest) {
    if (!data.createdAt) return <span className="text-slate italic">N/A</span>;
    const dateTimeString = getLocaleDateStringUTC(data.createdAt);
    return <div className="block whitespace-nowrap">{dateTimeString}</div>;
  }

  return (
    <BubbleOverlay loading={!data} hideBg loadingMinHeight="medium">
      {data && (
        <div className="GroupsGlance">
          <div className={`grid gap-4 grid-cols-${cols} justify-items-stretch`}>
            <div className="flex flex-col">
              <div className="bg-white shadow-md flex-grow rounded overflow-hidden">
                <div className="px-3 py-2 bg-ocean">
                  <h1 className="font-bold uppercase text-xs text-white text-right">
                    Group Details
                  </h1>
                </div>
                <div className="grid grid-cols-3 gap-4 py-4 px-5">
                  <BubbleFormGroup label={party_name} className="col-span-3">
                    <DefaultedText val={data.group.party.name} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label={group_id}>
                    <DefaultedText val={data.group.party.groupId} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label={group_type}>
                    <DefaultedText val={data.group.party.groupType?.name} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label="Created At">
                    {getLocaleDateStringUTC(data.group.date)}
                  </BubbleFormGroup>
                  <BubbleFormGroup label={group_contact_name}>
                    <DefaultedText val={data.group.party.contact} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label={guests}>
                    <DefaultedText val={data.group.party.size?.toString()} />
                  </BubbleFormGroup>
                  <BubbleFormGroup label={hospitality_desk}>
                    <span
                      className={`text-xs font-bold ${
                        data.group.party.hospitalityDesk ? 'text-lime-dark' : 'text-crimson'
                      }`}
                    >
                      {data.group.party.hospitalityDesk ? 'YES' : 'NO'}
                    </span>
                  </BubbleFormGroup>
                  <div className="col-span-3 grid grid-cols-2 gap-4 items-center">
                    <BubbleFormGroup label={tour_leader}>
                      <DefaultedText val={data.group.party.groupGuideName} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={tour_leader_cabin}>
                      <DefaultedText val={data.group.party.groupGuideCabin} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={sales_office}>
                      <DefaultedText val={data.group.salesOffice} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={agency_contact_name}>
                      <DefaultedText val={data.group.party.agencyContactName} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={sales_dept_phone}>
                      <DefaultedText val={data.group.party.salesPhone} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={sales_dept_email}>
                      <DefaultedText val={data.group.party.salesEmail} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={embark_date}>
                      <DefaultedText val={data.group.party.embark} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={debark_date}>
                      <DefaultedText val={data.group.party.debark} />
                    </BubbleFormGroup>
                  </div>
                  {showMoreGroupDetails && (
                    <>
                      <BubbleFormGroup label={cabin_drops} className="col-span-3">
                        <DefaultedText val={data.group.party.cabinDrops} />
                      </BubbleFormGroup>
                      <BubbleFormGroup label={dining_preference} className="col-span-3">
                        <DefaultedText val={data.group.party.diningPreference} />
                      </BubbleFormGroup>
                      <BubbleFormGroup label={guest_experience} className="col-span-3">
                        <DefaultedText html val={data.group.party.guestExperience} />
                      </BubbleFormGroup>
                      <BubbleFormGroup label={prepaid_packages} className="col-span-3">
                        <DefaultedText val={data.group.party.prepaidPackages} />
                      </BubbleFormGroup>
                      <BubbleFormGroup label={group_notes} className="col-span-3">
                        <DefaultedText val={data.group.party.notes} html block />
                      </BubbleFormGroup>
                      <BubbleFormGroup label={group_files} className="col-span-3 -mr-3 -mb-3">
                        {(data.group.party.files || []).length < 1 && (
                          <div className="mr-3 mb-3">
                            <span className="text-smoke italic">None</span>
                          </div>
                        )}
                        {(data.group.party.files || []).map((af, i) => (
                          <BubbleButton
                            key={i}
                            variant="slate"
                            inverse
                            size="small"
                            className="p-0-i underline text-left mr-3 mb-3"
                            linkProps={{ to: af.location, target: '_blank' }}
                          >
                            {af.name}
                          </BubbleButton>
                        ))}
                      </BubbleFormGroup>
                      <BubbleFormGroup label="Venue Attributes" className="col-span-3 -mr-3 -mb-3">
                        {(data.booking.venue.attributeFiles || []).length < 1 && (
                          <div className="mr-3 mb-3">
                            <span className="text-smoke italic">None</span>
                          </div>
                        )}
                        {(data.booking.venue.attributeFiles || []).map((af, i) => (
                          <BubbleButton
                            key={i}
                            variant="slate"
                            inverse
                            size="small"
                            className="p-0-i underline text-left mr-3 mb-3"
                            linkProps={{ to: af.location, target: '_blank' }}
                          >
                            {af.name}
                          </BubbleButton>
                        ))}
                      </BubbleFormGroup>
                    </>
                  )}
                </div>
                {!showMoreGroupDetails && (
                  <BubbleButton
                    variant="ocean"
                    block
                    inverse
                    className="col-span-3"
                    onClick={() => setShowMoreGroupDetails(true)}
                    icon="eye"
                  >
                    Show more details
                  </BubbleButton>
                )}
              </div>
              {data.booking.subgroup && (
                <div className="bg-white shadow-md flex-grow mt-4 rounded overflow-hidden">
                  <div className="px-3 py-2 bg-velvet">
                    <h1 className="font-bold uppercase text-xs text-white text-right">
                      Subgroup Details
                    </h1>
                  </div>
                  <div className="grid grid-cols-3 gap-4 py-4 px-5">
                    <BubbleFormGroup label="Subgroup Name" className="col-span-3">
                      <DefaultedText val={data.booking.subgroup.label} />
                      &nbsp;
                      <span>
                        (<DefaultedText val={data.booking.subgroup.name} />)
                      </span>
                    </BubbleFormGroup>
                    <BubbleFormGroup label={tour_leader}>
                      <DefaultedText val={data.booking.subgroup.groupGuideName} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label={tour_leader_cabin}>
                      <DefaultedText val={data.booking.subgroup.groupGuideCabin} />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Attendees">
                      <DefaultedText val={data.booking.subgroup.attendees.toString()} />
                    </BubbleFormGroup>
                    {showMoreSubgroupDetails && (
                      <>
                        <BubbleFormGroup label="Subgroup Notes" className="col-span-3">
                          <DefaultedText val={data.booking.subgroup.notes} block />
                        </BubbleFormGroup>
                        <BubbleFormGroup label="Subgroup Files" className="col-span-3 -mr-3 -mb-3">
                          {(data.booking.subgroup.files || []).length < 1 && (
                            <div className="mr-3 mb-3">
                              <span className="text-smoke italic">None</span>
                            </div>
                          )}
                          {(data.booking.subgroup.files || []).map((af, i) => (
                            <BubbleButton
                              key={i}
                              variant="slate"
                              inverse
                              size="small"
                              className="p-0-i underline text-left mr-3 mb-3"
                              linkProps={{ to: af.location, target: '_blank' }}
                            >
                              {af.name}
                            </BubbleButton>
                          ))}
                        </BubbleFormGroup>
                      </>
                    )}
                  </div>
                  {!showMoreSubgroupDetails && (
                    <BubbleButton
                      variant="velvet"
                      block
                      inverse
                      className="col-span-3"
                      onClick={() => setShowMoreSubgroupDetails(true)}
                      icon="eye"
                    >
                      Show more details
                    </BubbleButton>
                  )}
                </div>
              )}
            </div>
            <div className="bg-white shadow-md rounded overflow-hidden">
              <div className="px-3 py-2 bg-banana">
                <h1 className="font-bold uppercase text-xs text-white text-right">
                  Group Booking Details
                </h1>
              </div>
              <div className="grid gap-4 grid-cols-3 py-4 px-5">
                {canApprove && (
                  <BubbleFormGroup label="Change Status" className="col-span-3">
                    <BubbleOverlay loading={isBusy}>
                      <div className="flex rounded overflow-hidden">
                        <BubbleButton
                          size="small"
                          appearance="squared"
                          className="flex-grow uppercase text-xs font-bold-i"
                          onClick={() => handleRequestStatusChange('approved')}
                          variant="lime"
                          disabled={data.booking.status === 'approved'}
                          icon="thumbs-up"
                        >
                          Approve
                        </BubbleButton>
                        <BubbleButton
                          size="small"
                          appearance="squared"
                          className="flex-grow uppercase text-xs font-bold-i"
                          onClick={() => handleRequestStatusChange('rejected')}
                          variant="crimson"
                          disabled={data.booking.status === 'rejected'}
                          icon="thumbs-down"
                        >
                          Reject
                        </BubbleButton>
                      </div>
                    </BubbleOverlay>
                  </BubbleFormGroup>
                )}
                <BubbleFormGroup label="Group Event Name" className="col-span-3">
                  <DefaultedText val={data.booking.name} />
                </BubbleFormGroup>
                <BubbleFormGroup label="Date">
                  <DefaultedText val={getLocaleDateStringUTC(data.booking.date)} />
                </BubbleFormGroup>
                <BubbleFormGroup label="Start Time">
                  <DefaultedText
                    val={timeToString(data.booking.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT)}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="End Time">
                  <DefaultedText
                    val={timeToString(
                      calculateTimeOffset(data.booking.startTime, data.booking.duration).time,
                      hourFormat,
                      DEFAULT_TIMESTR_FORMAT
                    )}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label={guests}>
                  <DefaultedText val={data.booking.numGuests.toString()} />
                </BubbleFormGroup>
                <BubbleFormGroup label="Set up time (Minutes)">
                  <DefaultedText val={data.booking.bufferTime.setUpTime.toString()} />
                </BubbleFormGroup>
                <BubbleFormGroup label="Breakdown Time (minutes)">
                  <DefaultedText val={data.booking.bufferTime.breakdownTime.toString()} />
                </BubbleFormGroup>
                <div className="col-span-3 grid grid-cols-2 gap-4">
                  <BubbleFormGroup label="Status">
                    {makeTextFromStatus(data.booking.status)}
                  </BubbleFormGroup>
                  <BubbleFormGroup label="Requester">
                    {makeFullName(
                      data.booking.createdBy.firstName,
                      data.booking.createdBy.lastName
                    )}
                  </BubbleFormGroup>
                </div>
                <BubbleFormGroup label="Created At" className="col-span-3">
                  {groupBookingCreatedAtSection(data.booking)}
                </BubbleFormGroup>
                <BubbleFormGroup label="Voyage" className="col-span-3">
                  {`${data.sailingBundle?.sailing.name ?? 'Unknown'} (Day ${
                    data.booking.dayIndex + 1
                  })`}
                </BubbleFormGroup>
                <BubbleFormGroup label="Location" className="col-span-3">
                  <DefaultedText
                    val={data.sailingBundle ? getDayLocation(data.sailingBundle.day) : undefined}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Venue" className="col-span-3">
                  <DefaultedText val={data.booking.venue.name} />
                  {data.booking.venueConfig
                    ? `, ${data.booking.venueConfig.name} (occ. ${data.booking.venueConfig.occupancy})`
                    : `, default (occ. ${data.booking.venue.occupancy})`}
                </BubbleFormGroup>
                {showMoreBookingDetails && (
                  <>
                    <BubbleFormGroup label="Food & Beverages" className="col-span-3">
                      <DefaultedText
                        val={data.booking.resources
                          .filter(resource => resource.resourceType === GroupResourceType.FB)
                          .map(r => `${r.name} ($${r.price}) (amt. ${r.amount})`)
                          .join(', ')}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Entertainment" className="col-span-3">
                      <DefaultedText
                        val={data.booking.resources
                          .filter(resource => resource.resourceType === GroupResourceType.ENT)
                          .map(r => `${r.name} ($${r.price}) (amt. ${r.amount})`)
                          .join(', ')}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Photography" className="col-span-3">
                      <DefaultedText
                        val={data.booking.resources
                          .filter(resource =>
                            ['Photography', 'photogtaphy'].includes(resource.resourceType)
                          )
                          .map(r => `${r.name} ($${r.price}) (amt. ${r.amount})`)
                          .join(', ')}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Housekeeping Requests" className="col-span-3">
                      <DefaultedText
                        val={data.booking.resources
                          .filter(resource => resource.resourceType === GroupResourceType.HSK)
                          .map(r => `${r.name} ($${r.price}) (amt. ${r.amount})`)
                          .join(', ')}
                      />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Other Resources" className="col-span-3">
                      {otherGroupResourceSection(data.booking.resources)}
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Group Event Notes" className="col-span-3">
                      <DefaultedText val={data.booking.notes} html block />
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Group Event Files" className="col-span-3 -mr-3 -mb-3">
                      {data.booking.files.length < 1 && (
                        <div className="mr-3 mb-3">
                          <span className="text-smoke italic">None</span>
                        </div>
                      )}
                      {data.booking.files.map((rf, i) => (
                        <BubbleButton
                          key={i}
                          variant="slate"
                          inverse
                          size="small"
                          className="p-0-i underline text-left mr-3 mb-3"
                          linkProps={{ to: rf.location, target: '_blank' }}
                        >
                          {rf.name}
                        </BubbleButton>
                      ))}
                    </BubbleFormGroup>
                  </>
                )}
              </div>
              {!showMoreBookingDetails && (
                <BubbleButton
                  variant="banana"
                  block
                  inverse
                  className="col-span-3"
                  onClick={() => setShowMoreBookingDetails(true)}
                  icon="eye"
                >
                  Show more details
                </BubbleButton>
              )}
            </div>
            {!props.hideComments && (
              <div className="bg-grey-lightest shadow-md flex flex-col rounded overflow-hidden">
                <div className="px-3 py-2 bg-lime">
                  <h1 className="font-bold uppercase text-xs text-white text-right">Comments</h1>
                </div>
                <div className="flex-grow relative">
                  <GroupRequestFormBody
                    loggedUser={props.user}
                    comments={data.booking.comments}
                    onRequestComment={handleRequestComment}
                  />
                </div>
              </div>
            )}
            {!props.hideVenueDayView && data.sailingBundle && (
              <div className={`col-span-${cols}`}>
                <div className="text-center relative mb-3">
                  <BubbleButton
                    variant="navy"
                    inverse
                    onClick={() => setShowContext(!showContext)}
                    iconSuffix={showContext ? 'chevron-up' : 'chevron-down'}
                  >
                    {`${showContext ? 'Hide' : 'Show'} Venue Day View`}
                  </BubbleButton>
                </div>
                {showContext && (
                  <div className="bg-white shadow-md mb-3">
                    <GroupBookingsGrid
                      locked
                      hourFormat={hourFormat}
                      languages={languages.map(l => l.key)}
                      defaultLanguage={defaultLanguage.key}
                      bufferTime={data.booking.bufferTime}
                      groupBookingDate={dateToIsoUTC(data.booking.date)}
                      groupBookingStartTime={data.booking.startTime}
                      groupBookingEndTime={addMinutesToTime(
                        data.booking.startTime,
                        data.booking.duration
                      )}
                      days={data.sailingBundle.sailing.days.length}
                      sailing={data.sailingBundle.sailing}
                      omitted={[data.booking._id]}
                      venue={data.booking.venue}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </BubbleOverlay>
  );
}
